import { useEffect, useMemo, useState } from 'react';
import { Alert, Drawer, V2Button, V2FormActions, V2FormField } from '@sakhtar/design-system/react';
import { ApiError, apiPut } from '../../lib/apiClient.js';
import {
  diffKeys,
  groupKeysByEntity,
  permissionActionLabel,
  sameKeySet,
  toggleKey,
  toggleKeys,
} from '../../lib/rolePermissionEditor.js';
import type {
  CustomRoleCreatedResponse,
  PermissionCatalogGroup,
  RoleTemplateSummary,
} from '../../lib/types/membership.js';

interface RoleEditorDrawerProps {
  readonly open: boolean;
  readonly base: string;
  /** Custom (`custom-*`) roles only; built-in templates are never editable. */
  readonly roles: readonly RoleTemplateSummary[];
  readonly groups: readonly PermissionCatalogGroup[];
  readonly onClose: () => void;
  /** Called after a successful save so the caller can reload the role list. */
  readonly onSaved: () => void;
}

function saveErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return 'فقط مدیر سازمان می‌تواند نقش‌ها را ویرایش کند.';
    if (error.status === 400) return 'درخواست نامعتبر است؛ حداقل یک دسترسی باید انتخاب شود.';
  }
  return 'ذخیره دسترسی‌ها ناموفق بود. دوباره تلاش کنید.';
}

export function RoleEditorDrawer({
  open,
  base,
  roles,
  groups,
  onClose,
  onSaved,
}: RoleEditorDrawerProps) {
  const [roleId, setRoleId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const role = roles.find((r) => r.roleId === roleId);
  const original = useMemo(() => new Set(role?.permissionKeys ?? []), [role]);

  // Load the chosen role's current grants into the form.
  useEffect(() => {
    setSelected(new Set(role?.permissionKeys ?? []));
    setDisplayName(role?.displayName ?? '');
    setError(null);
    setSaved(false);
  }, [role]);

  // Default to the first custom role when the drawer opens.
  useEffect(() => {
    if (open && roleId === '' && roles[0]) setRoleId(roles[0].roleId);
    if (!open) setRoleId('');
  }, [open, roleId, roles]);

  const nameChanged = role !== undefined && displayName.trim() !== role.displayName;
  const keysChanged = !sameKeySet(original, selected);
  const diff = diffKeys(original, selected);
  const canSave =
    role !== undefined &&
    !saving &&
    selected.size > 0 &&
    displayName.trim() !== '' &&
    (keysChanged || nameChanged);

  async function save() {
    if (!role) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await apiPut<CustomRoleCreatedResponse>(`${base}/api/v1/roles/${role.roleId}`, {
        displayName: displayName.trim(),
        permissionKeys: [...selected],
      });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(saveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      open={open}
      title="ویرایش دسترسی نقش‌ها"
      onClose={onClose}
      footer={
        <V2FormActions>
          <V2Button variant="secondary" onClick={onClose} disabled={saving}>
            بستن
          </V2Button>
          <V2Button variant="primary" onClick={() => void save()} disabled={!canSave}>
            {saving ? 'در حال ذخیره…' : 'ذخیره تغییرات'}
          </V2Button>
        </V2FormActions>
      }
    >
      {roles.length === 0 ? (
        <Alert tone="info">
          هنوز نقش سفارشی ساخته نشده است. با «دعوت همکار جدید» و انتخاب دسترسی‌ها، نقش سفارشی ایجاد
          می‌شود.
        </Alert>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <V2FormField label="نقش" width="full">
            <select
              className="sk-select"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              disabled={saving}
            >
              {roles.map((r) => (
                <option key={r.roleId} value={r.roleId}>
                  {r.displayName}
                </option>
              ))}
            </select>
          </V2FormField>

          <V2FormField label="عنوان نقش" required width="full">
            <input
              className="sk-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={saving}
            />
          </V2FormField>

          <Alert tone="warning">
            تغییر دسترسی‌ها برای همه کاربرانی که این نقش را دارند اعمال می‌شود (از ورود یا بازخوانی
            بعدی آن‌ها).
          </Alert>

          {groups.map((group) => {
            const groupOn = group.keys.filter((key) => selected.has(key)).length;
            return (
              <details key={group.id} open={groupOn > 0} style={{ display: 'block' }}>
                <summary
                  style={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 'var(--sk-fs-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  {group.label}
                  <span style={{ color: 'var(--sk-fg-muted)', fontWeight: 400 }}>
                    {groupOn.toLocaleString('fa-IR')} از {group.keys.length.toLocaleString('fa-IR')}
                  </span>
                </summary>
                <div
                  style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}
                >
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--sm"
                    style={{ alignSelf: 'flex-start' }}
                    disabled={saving}
                    onClick={() => setSelected((prev) => toggleKeys(prev, group.keys))}
                  >
                    {groupOn === group.keys.length ? 'حذف همه این بخش' : 'انتخاب همه این بخش'}
                  </button>
                  {groupKeysByEntity(group.keys).map((entityGroup) => (
                    <div key={entityGroup.entity}>
                      <div
                        dir="ltr"
                        style={{
                          fontSize: 'var(--sk-fs-xs)',
                          color: 'var(--sk-fg-muted)',
                          textAlign: 'right',
                          marginBottom: 4,
                        }}
                      >
                        {entityGroup.entity}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
                        {entityGroup.keys.map((key) => (
                          <label
                            key={key}
                            title={key}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              className="sk-check"
                              checked={selected.has(key)}
                              disabled={saving}
                              onChange={() => setSelected((prev) => toggleKey(prev, key))}
                            />
                            {permissionActionLabel(key)}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            );
          })}

          {keysChanged ? (
            <div style={{ fontSize: 'var(--sk-fs-xs)', color: 'var(--sk-fg-muted)' }}>
              {diff.added.length.toLocaleString('fa-IR')} دسترسی اضافه و{' '}
              {diff.removed.length.toLocaleString('fa-IR')} دسترسی حذف می‌شود.
            </div>
          ) : null}
          {selected.size === 0 ? (
            <Alert tone="warning">حداقل یک دسترسی باید انتخاب شود.</Alert>
          ) : null}
          {error ? (
            <Alert tone="danger" title="خطا">
              {error}
            </Alert>
          ) : null}
          {saved ? <Alert tone="success">دسترسی‌های نقش ذخیره شد.</Alert> : null}
        </div>
      )}
    </Drawer>
  );
}
